import { SwipeableRow, TrashIcon } from '@tunein/react-ui';
import type { ChildGuideItem } from '@tunein/web-api-client';
import type { ReactElement } from 'react';
import { useContext } from 'react';
import { removeRecent } from 'src/common/actions/browse';
import { showMessage } from 'src/common/actions/messagePopover';
import {
  CLEAR_RECENTS_ERROR,
  CLEAR_SINGLE_RECENT_BUTTON,
} from 'src/common/constants/localizations/settings';
import useActions from 'src/common/hooks/useActions';
import { LocationAndLocalizationContext } from '../../../../providers/LocationAndLocalizationProvider';
import css from './guide-item-swipe-action.module.scss';

// Per the Figma design for the Recents page.
const REVEAL_WIDTH = 82;

interface RemoveRecentActions {
  removeRecent: (guideId: string) => Promise<unknown>;
  showMessage: (message: string) => void;
}

export interface GuideItemSwipeActionComponentProps {
  guideItem: ChildGuideItem;
  children: ReactElement;
}

/**
 * Removes a single item from recents when its row is swiped away. Only rendered when the API points
 * the item's `swipe` behavior at `RemoveRecent` (see GuideItemSwipeAction).
 */
export function RemoveRecentSwipeAction({
  guideItem,
  children,
}: GuideItemSwipeActionComponentProps) {
  const { getLocalizedText } = useContext(LocationAndLocalizationContext);
  const actions = useActions<RemoveRecentActions>({ removeRecent, showMessage });

  // Platform sends the id to delete on the action itself; fall back to the item's own guideId.
  const guideId = guideItem.actions?.removeRecent?.guideId || guideItem.guideId;

  if (!guideId) {
    return children;
  }

  const handleSwipe = async () => {
    try {
      await actions.removeRecent(guideId);
    } catch (error) {
      actions.showMessage(CLEAR_RECENTS_ERROR);
      // Rethrown so SwipeableRow puts the row back rather than leaving it swiped away.
      throw error;
    }
  };

  return (
    <SwipeableRow
      action={<TrashIcon className={css.actionIcon} data-testid="removeRecentSwipeIcon" />}
      actionLabel={getLocalizedText(CLEAR_SINGLE_RECENT_BUTTON)}
      classNames={{ root: css.removeAction }}
      onAction={handleSwipe}
      revealWidth={REVEAL_WIDTH}
    >
      {children}
    </SwipeableRow>
  );
}
